import React from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import pb from '../../../lib/pocketbase';

const emptyForm = {
  name: '',
  description: '',
  price_label: '',
  price_amount: '',
  billing_period: 'mois',
  price_tax_label: '',
  featuresText: '',
  is_featured: false,
  is_active: true,
  sort_order: 0,
  cta_label: '',
  cta_url: '',
  cta_open_in_new_tab: false,
};

function normalizeFeatures(text) {
  const lines = String(text || '')
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean);
  return lines;
}

function normalizeCtaUrl(input) {
  const raw = String(input || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  const origin = (typeof window !== 'undefined' && window.location && window.location.origin)
    ? window.location.origin
    : '';
  if (raw.startsWith('/')) return origin ? `${origin}${raw}` : raw;
  const slug = raw
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\-_]/g, '')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
  if (!slug) return '';
  const path = `/${slug}`;
  return origin ? `${origin}${path}` : path;
}

function AdminSitePricingPlansPage() {
  const [allowEmpty, setAllowEmpty] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const dragIndexRef = React.useRef(-1);

  const [items, setItems] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);

  const fetchAll = async () => {
    setError('');
    try {
      const res = await pb.collection('site_pricing_plans').getList(1, 200, { sort: 'sort_order, name' });
      setItems(res?.items ?? []);
    } catch (e) {
      setError(e?.message || 'Erreur chargement des plans');
      setItems([]);
    }
  };

  React.useEffect(() => {
    fetchAll();
    const t = setTimeout(() => setAllowEmpty(true), 250);
    return () => clearTimeout(t);
  }, []);

  const persistOrder = async (orderedItems) => {
    try {
      await Promise.all(
        orderedItems.map((rec, idx) => pb.collection('site_pricing_plans').update(rec.id, { sort_order: idx + 1 }))
      );
    } catch (e) {
      alert(e?.message || 'Erreur de ré-ordre');
    }
  };

  const onDragStart = (e, index) => {
    dragIndexRef.current = index;
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, overIndex) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from === -1 || from === overIndex) return;
    setItems((arr) => {
      const next = arr.slice();
      const [moved] = next.splice(from, 1);
      next.splice(overIndex, 0, moved);
      dragIndexRef.current = overIndex;
      return next;
    });
  };

  const onDrop = async () => {
    dragIndexRef.current = -1;
    const ordered = [...items];
    await persistOrder(ordered);
    await fetchAll();
  };

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      sort_order: items.length ? Math.max(...items.map(i => Number(i.sort_order || 0))) + 1 : 0,
    });
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    const features = Array.isArray(it?.features) ? it.features : (it?.features ? it.features : []);
    setForm({
      name: it?.name || '',
      description: it?.description || '',
      price_label: it?.price_label || '',
      price_amount: (it?.price_amount ?? it?.price_amount === 0) ? String(it.price_amount) : '',
      billing_period: it?.billing_period || 'mois',
      price_tax_label: it?.price_tax_label || '',
      featuresText: Array.isArray(features) ? features.join('\n') : '',
      is_featured: !!it?.is_featured,
      is_active: it?.is_active !== false,
      sort_order: Number(it?.sort_order || 0),
      cta_label: it?.cta_label || '',
      cta_url: it?.cta_url || '',
      cta_open_in_new_tab: !!it?.cta_open_in_new_tab,
    });
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        name: form.name || '',
        description: form.description || '',
        price_label: form.price_label || '',
        price_amount: form.price_amount === '' ? null : Number(form.price_amount),
        billing_period: form.billing_period || 'mois',
        price_tax_label: form.price_tax_label || '',
        features: normalizeFeatures(form.featuresText),
        is_featured: !!form.is_featured,
        is_active: !!form.is_active,
        sort_order: Number(form.sort_order || 0),
        cta_label: form.cta_label || '',
        cta_url: normalizeCtaUrl(form.cta_url),
        cta_open_in_new_tab: !!form.cta_open_in_new_tab,
      };
      if (editing?.id) await pb.collection('site_pricing_plans').update(editing.id, payload);
      else await pb.collection('site_pricing_plans').create(payload);
      await fetchAll();
      closeModal();
    } catch (e) {
      alert(e?.message || "Erreur d'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce plan ?')) return;
    try {
      await pb.collection('site_pricing_plans').delete(id);
      await fetchAll();
    } catch (e) {
      alert(e?.message || 'Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (it) => {
    try {
      await pb.collection('site_pricing_plans').update(it.id, { is_active: !(it?.is_active !== false) });
      await fetchAll();
    } catch (e) {
      alert(e?.message || "Erreur de mise à jour");
    }
  };

  const handleToggleFeatured = async (it) => {
    try {
      await pb.collection('site_pricing_plans').update(it.id, { is_featured: !(it?.is_featured === true) });
      await fetchAll();
    } catch (e) {
      alert(e?.message || "Erreur de mise à jour");
    }
  };

  const move = async (id, dir) => {
    const ordered = [...items].sort((a, b) => {
      const ao = Number(a.sort_order || 0);
      const bo = Number(b.sort_order || 0);
      if (ao !== bo) return ao - bo;
      return (a.created || '').localeCompare(b.created || '');
    });
    const idx = ordered.findIndex(i => i.id === id);
    if (idx < 0) return;
    const swapWith = dir === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= ordered.length) return;
    const a = ordered[idx];
    const b = ordered[swapWith];
    const ao = Number(a.sort_order || 0);
    const bo = Number(b.sort_order || 0);
    try {
      await Promise.all([
        pb.collection('site_pricing_plans').update(a.id, { sort_order: bo }),
        pb.collection('site_pricing_plans').update(b.id, { sort_order: ao }),
      ]);
      await fetchAll();
    } catch (e) {
      alert(e?.message || 'Erreur de ré-ordre');
    }
  };

  return (
    <AdminLayout title="">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ marginTop: 0 }}>Tarifs · Plans</h1>
          <p style={{ marginTop: 6 }}>Gérez les plans (actif, mise en avant, ordre).</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <button
            className="btn-primary"
            onClick={() => { setEditing(null); openCreate(); }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              borderRadius: 10,
              background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
              border: '1px solid rgba(79,70,229,0.6)',
              boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
              color: 'white',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 999, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </span>
            <span>Nouveau plan</span>
          </button>
        </div>
      </div>

      <style>{`
        .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.04); }
        .btn-primary:active { transform: translateY(0); filter: brightness(0.98); }
        .header-pill{ cursor:pointer; background: transparent; border:1px solid transparent; color: var(--admin-text); padding:8px 10px; border-radius:10px; }
        .header-pill:hover{ background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .admin-shell.admin-light .header-pill:hover{ background: rgba(2,6,23,0.06); }
        .toggle-pill{ cursor:pointer; border:1px solid var(--admin-border); background: var(--admin-panel); color: var(--admin-text); padding:6px 10px; border-radius:999px; font-size:.85rem; }
        .toggle-pill.on{ border-color: rgba(34,197,94,0.55); box-shadow: 0 0 0 3px rgba(34,197,94,0.15); }
        .toggle-pill.off{ opacity:.8; }
        .badge{ padding:4px 8px; border-radius:999px; border:1px solid var(--admin-border); font-size:.8rem; }
        .grid{ display:grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 12px; }
        @media (max-width: 480px){ .grid{ grid-template-columns: 1fr; } }

        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; }
        .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 980px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid var(--admin-border); }
        .modal-body{ padding: 16px 20px; display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .modal-body .full{ grid-column: 1 / -1; }
        .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 8px; border-radius: 6px; }
        .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .modal-actions { display:flex; gap: 12px; justify-content:flex-end; padding: 16px 20px; border-top:1px solid var(--admin-border); }
        .field{ display:flex; flex-direction:column; gap: 6px; }
        .field label{ font-size: .9rem; color: var(--admin-muted); }
        .field input[type="text"], .field input[type="number"], .field textarea, .field select{
          background: var(--admin-panel-2);
          border:1px solid var(--admin-border);
          color: var(--admin-text);
          border-radius: 10px;
          padding: 10px 10px;
          outline:none;
        }
        .field textarea{ min-height: 140px; resize: vertical; }
        .field input:focus, .field textarea:focus, .field select:focus{ border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
        .field small{ color: var(--admin-muted); }
        .btn{ cursor:pointer; border:1px solid var(--admin-border); background: transparent; color: var(--admin-text); padding: 10px 12px; border-radius: 10px; }
        .btn:hover{ background: rgba(255,255,255,0.06); }
        .admin-shell.admin-light .btn:hover{ background: rgba(2,6,23,0.06); }
        .btn-solid{ border-color: rgba(79,70,229,0.6); background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85)); color:#fff; }
        .btn-solid:disabled{ opacity: .6; cursor: not-allowed; }
        .btn-danger{ border-color: rgba(239,68,68,0.5); }
      `}</style>

      {error && <div style={{ color: '#ef4444', marginBottom: 8 }}>{error}</div>}

      <div className="card" style={{ marginTop: 12 }}>
        <div className="grid">
          {items.map((it, idx) => {
              const active = it?.is_active !== false;
              const featured = it?.is_featured === true;
              return (
                <div
                  key={it.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, idx)}
                  onDragOver={(e) => onDragOver(e, idx)}
                  onDrop={(e) => {
                    e.preventDefault();
                    onDrop();
                  }}
                  title={`Glissez pour réordonner (position ${idx + 1})`}
                  style={{ border: '1px solid var(--admin-border)', borderRadius: 12, padding: 12, background: 'linear-gradient(180deg,var(--admin-panel),var(--admin-panel-2))', cursor: 'grab' }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 800, fontSize: '1.02rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.name || '—'}</div>
                      <div style={{ color: 'var(--admin-muted)', marginTop: 4, fontSize: '.9rem' }}>
                        {it.price_label ? it.price_label : '—'}{(typeof it.price_amount === 'number') ? ` · ${it.price_amount}€` : ''} · {it.billing_period || '—'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {featured && <span className="badge">⭐ Mis en avant</span>}
                      <button className={'toggle-pill ' + (active ? 'on' : 'off')} onClick={() => handleToggleActive(it)} title={active ? 'Désactiver' : 'Activer'}>
                        {active ? 'Actif' : 'Inactif'}
                      </button>
                    </div>
                  </div>

                  {it.description && (
                    <div style={{ marginTop: 10, color: 'var(--admin-muted)', fontSize: '.9rem', lineHeight: 1.3, maxHeight: 60, overflow: 'hidden' }}>
                      {it.description}
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    <button className="header-pill" onClick={() => move(it.id, 'up')} title="Monter">⬆</button>
                    <button className="header-pill" onClick={() => move(it.id, 'down')} title="Descendre">⬇</button>
                    <span style={{ color: 'var(--admin-muted)', fontSize: '.85rem' }}>Ordre: {idx + 1}</span>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                      <button className="header-pill" title="Mettre en avant" onClick={() => handleToggleFeatured(it)}>⭐</button>
                      <button className="header-pill" title="Modifier" onClick={() => openEdit(it)}>✏️</button>
                      <button className="header-pill" title="Supprimer" onClick={() => handleDelete(it.id)}>🗑️</button>
                    </div>
                  </div>
                </div>
              );
            })}

          {items.length === 0 && allowEmpty && (
            <div style={{ gridColumn: '1 / -1', padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220, width: '100%' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <h3 style={{ margin: 0 }}>Aucun plan</h3>
                <p style={{ margin: 0, color: 'var(--admin-muted)' }}>Commencez par ajouter votre premier plan.</p>
                <button className="btn-primary" onClick={openCreate} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 14px', borderRadius: 10,
                  background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
                  border: '1px solid rgba(79,70,229,0.6)',
                  boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
                  color: 'white'
                }}>
                  <span>Nouveau plan</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {open && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={(e) => {
          if (e.target?.classList?.contains('modal-overlay')) closeModal();
        }}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editing?.id ? 'Modifier le plan' : 'Nouveau plan'}</h3>
              <button className="close-btn" onClick={closeModal} aria-label="Fermer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="field full">
                <label>Nom</label>
                <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="field full">
                <label>Description</label>
                <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="field">
                <label>Price label</label>
                <input type="text" value={form.price_label} onChange={(e) => setForm(f => ({ ...f, price_label: e.target.value }))} placeholder="ex: À partir de" />
              </div>
              <div className="field">
                <label>Prix (montant)</label>
                <input type="number" value={form.price_amount} onChange={(e) => setForm(f => ({ ...f, price_amount: e.target.value }))} placeholder="ex: 49" />
              </div>
              <div className="field">
                <label>Période</label>
                <select value={form.billing_period} onChange={(e) => setForm(f => ({ ...f, billing_period: e.target.value }))}>
                  <option value="mois">mois</option>
                  <option value="an">an</option>
                  <option value="unique">unique</option>
                  <option value="sur_devis">sur_devis</option>
                </select>
              </div>
              <div className="field">
                <label>Mention prix</label>
                <select value={form.price_tax_label} onChange={(e) => setForm(f => ({ ...f, price_tax_label: e.target.value }))}>
                  <option value="">(Aucune)</option>
                  <option value="HT">HT</option>
                  <option value="TTC">TTC</option>
                </select>
              </div>
              <div className="field">
                <label>Ordre (sort_order)</label>
                <input type="number" value={form.sort_order} onChange={(e) => setForm(f => ({ ...f, sort_order: e.target.value }))} />
              </div>
              <div className="field full">
                <label>Features (1 par ligne)</label>
                <textarea value={form.featuresText} onChange={(e) => setForm(f => ({ ...f, featuresText: e.target.value }))} placeholder="Feature 1\nFeature 2\n..." />
              </div>
              <div className="field">
                <label>Bouton (label)</label>
                <input type="text" value={form.cta_label} onChange={(e) => setForm(f => ({ ...f, cta_label: e.target.value }))} placeholder="ex: Choisir ce plan" />
              </div>
              <div className="field">
                <label>Bouton (lien)</label>
                <input type="text" value={form.cta_url} onChange={(e) => setForm(f => ({ ...f, cta_url: e.target.value }))} placeholder="ex: /contact ou https://..." />
                <small>Astuce: tu peux mettre /contact, ça sera normalisé à l’enregistrement.</small>
              </div>
              <div className="field">
                <label>Ouvrir dans un nouvel onglet</label>
                <input type="checkbox" checked={!!form.cta_open_in_new_tab} onChange={(e) => setForm(f => ({ ...f, cta_open_in_new_tab: e.target.checked }))} />
              </div>
              <div className="field">
                <label>Actif</label>
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))} />
              </div>
              <div className="field">
                <label>Mis en avant</label>
                <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm(f => ({ ...f, is_featured: e.target.checked }))} />
              </div>
            </div>
            <div className="modal-actions">
              {editing?.id && (
                <button className="btn btn-danger" onClick={() => { closeModal(); handleDelete(editing.id); }}>Supprimer</button>
              )}
              <button className="btn" onClick={closeModal}>Annuler</button>
              <button className="btn btn-solid" onClick={handleSave} disabled={loading}>
                {loading ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminSitePricingPlansPage;
